import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import {
  Loader2, Star, Check, X, MessageSquare, ChevronDown, Building2,
  ChevronsDownUp, ChevronsUpDown, CheckSquare, Languages, ArrowUpDown, MessageCircle, RefreshCw, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { useIsMobile } from "@/hooks/use-mobile";
import { getDotPatternStyle } from "@/components/AppBackground";
import EmojiPickerPopover from "@/components/onboarding/EmojiPickerPopover";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import {
  getGmbBusinesses,
  updateGmbBusiness,
  getGmbReviews,
  updateGmbReviewText,
  translateGmbText,
  regenerateGmbReview,
  approveGmbReview,
  rejectGmbReview,
} from "@/lib/api";

const DEFAULT_EMOJI = "🏢";
const RATING_WORDS = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

const TRANSLATE_LANGUAGES = [
  "Castellano",
  "Inglés",
  "Francés",
  "Valenciano",
  "Catalán",
  "Alemán",
  "Holandés",
  "Italiano",
  "Portugués",
];

const SORT_OPTIONS = [
  { value: "default", label: "Pendientes primero" },
  { value: "recent", label: "Más recientes" },
  { value: "oldest", label: "Más antiguas" },
  { value: "rating_desc", label: "Mejor valoración" },
  { value: "rating_asc", label: "Peor valoración" },
];

function dateKey(r) {
  return String(r.review_date || r.created_at || "");
}

/** Quita el bloque "(Translated by Google)" que Google añade a las reseñas en otro idioma. */
function cleanComment(comment) {
  const c = (comment || "").trim();
  if (!c) return "Sin comentario";
  const origIdx = c.indexOf("(Original)");
  if (origIdx !== -1) {
    const original = c.slice(origIdx + "(Original)".length).trim();
    if (original) return original;
  }
  const transIdx = c.indexOf("(Translated by Google)");
  if (transIdx > 0) return c.slice(0, transIdx).trim();
  return c;
}

function buildWhatsAppMessage(review, responseText) {
  const stars = ratingToStars(review.rating);
  const reviewer = review.reviewer_name || "Anónimo";
  const comment = cleanComment(review.comment);
  const respuesta = (responseText || review.edited_text || review.suggested_text || "").trim();
  return (
    `🔔 Brutal te informa: has recibido una reseña negativa en Google.\n\n` +
    `⭐ Valoración: ${stars}/5\n` +
    `👤 ${reviewer}\n` +
    `💬 "${comment}"\n\n` +
    `✍️ Esta es la respuesta que hemos preparado:\n"${respuesta}"\n\n` +
    `Si quieres que contestemos otra cosa, responde a este mensaje y lo ajustamos. 🙌`
  );
}

function waUrl(phone, text) {
  const clean = String(phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${clean}?text=${encodeURIComponent(text || "")}`;
}

function ratingToStars(rating) {
  if (rating == null) return 0;
  const word = String(rating).trim().toUpperCase();
  if (word in RATING_WORDS) return RATING_WORDS[word];
  const n = parseInt(word, 10);
  return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
}

const isNegativeReview = (r) => ratingToStars(r.rating) <= 3;

const STATUS_META = {
  pending: { label: "Pendiente", variant: "secondary" },
  approved: { label: "Aprobada", variant: "secondary" },
  published: { label: "Publicada", variant: "default" },
  rejected: { label: "Rechazada", variant: "outline" },
  error: { label: "Error", variant: "destructive" },
};

function formatFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${d.getFullYear()}`;
}

function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0" aria-label={`${value} de 5 estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
          strokeWidth={1.5}
          aria-hidden
        />
      ))}
    </span>
  );
}

export default function GmbReviewsPage() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const [businesses, setBusinesses] = useState([]);
  const [loadingBiz, setLoadingBiz] = useState(true);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [tab, setTab] = useState("neg_pending"); // neg_pending | pos_pending | all
  const [sortBy, setSortBy] = useState("default");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [drafts, setDrafts] = useState({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [acting, setActing] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [editingIds, setEditingIds] = useState(() => new Set());
  const [confirmRepublish, setConfirmRepublish] = useState(null);
  const [waModal, setWaModal] = useState(null); // reseña para avisar por WhatsApp
  const [waText, setWaText] = useState("");
  const [waPhone, setWaPhone] = useState("");
  // Móvil: lista de clientes y reseñas no conviven; en escritorio se ignora (md: siempre ambos)
  const [mobileShowList, setMobileShowList] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.gmb === false) navigate("/candidatos", { replace: true });
  }, [authLoading, user, navigate]);

  const loadBusinesses = useCallback(() => {
    if (!user || user.permissions?.gmb === false) return;
    setLoadingBiz(true);
    getGmbBusinesses()
      .then((data) => {
        const list = data.businesses || [];
        setBusinesses(list);
        setSelectedBiz((prev) => prev || (list[0]?.business_id ?? null));
      })
      .catch(() => {
        toast.error("Error al cargar negocios");
        setBusinesses([]);
      })
      .finally(() => setLoadingBiz(false));
  }, [user]);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const loadReviews = useCallback(() => {
    if (!selectedBiz) {
      setReviews([]);
      return;
    }
    setLoadingReviews(true);
    getGmbReviews({ business_id: selectedBiz })
      .then((data) => setReviews(data.reviews || []))
      .catch(() => {
        toast.error("Error al cargar reseñas");
        setReviews([]);
      })
      .finally(() => setLoadingReviews(false));
  }, [selectedBiz]);

  useEffect(() => {
    setExpandedIds(new Set());
    setSelectionMode(false);
    setSelectedIds(new Set());
    loadReviews();
  }, [loadReviews]);

  const counts = useMemo(() => {
    let neg = 0, pos = 0;
    for (const r of reviews) {
      if (r.status !== "pending") continue;
      if (isNegativeReview(r)) neg += 1; else pos += 1;
    }
    return { neg, pos };
  }, [reviews]);

  const visibleReviews = useMemo(() => {
    const list = reviews.filter((r) => {
      if (tab === "all") return true;
      if (r.status !== "pending") return false;
      return tab === "neg_pending" ? isNegativeReview(r) : !isNegativeReview(r);
    });
    const dDesc = (a, b) => dateKey(b).localeCompare(dateKey(a));
    const dAsc = (a, b) => dateKey(a).localeCompare(dateKey(b));
    return [...list].sort((a, b) => {
      if (sortBy === "recent") return dDesc(a, b);
      if (sortBy === "oldest") return dAsc(a, b);
      if (sortBy === "rating_desc") return ratingToStars(b.rating) - ratingToStars(a.rating) || dDesc(a, b);
      if (sortBy === "rating_asc") return ratingToStars(a.rating) - ratingToStars(b.rating) || dDesc(a, b);
      // default: pendientes primero, luego por fecha desc
      const rank = (r) => (r.status === "pending" ? 0 : 1);
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return dDesc(a, b);
    });
  }, [reviews, tab, sortBy]);

  const pendingVisible = useMemo(() => visibleReviews.filter((r) => r.status === "pending"), [visibleReviews]);
  const allExpanded = visibleReviews.length > 0 && visibleReviews.every((r) => expandedIds.has(r.id));

  const draftFor = (r) => drafts[r.id] ?? r.edited_text ?? r.suggested_text ?? "";

  const handleSelectEmoji = async (businessId, emoji) => {
    setBusinesses((prev) => prev.map((b) => (b.business_id === businessId ? { ...b, emoji } : b)));
    try {
      await updateGmbBusiness(businessId, { emoji });
    } catch (_) {
      toast.error("No se pudo guardar el emoji");
      loadBusinesses();
    }
  };

  const setExpanded = (id, open) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleExpand = (review) => {
    const open = !expandedIds.has(review.id);
    setExpanded(review.id, open);
    if (open && drafts[review.id] == null) {
      setDrafts((prev) => ({ ...prev, [review.id]: review.edited_text ?? review.suggested_text ?? "" }));
    }
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(visibleReviews.map((r) => r.id)));
      setDrafts((prev) => {
        const next = { ...prev };
        for (const r of visibleReviews) {
          if (next[r.id] == null) next[r.id] = r.edited_text ?? r.suggested_text ?? "";
        }
        return next;
      });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allPendingSelected = pendingVisible.length > 0 && pendingVisible.every((r) => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    setSelectedIds(allPendingSelected ? new Set() : new Set(pendingVisible.map((r) => r.id)));
  };

  const handleSaveDraft = async (review) => {
    if (review.status !== "pending") return;
    const text = drafts[review.id];
    if (text == null || text === (review.edited_text ?? review.suggested_text ?? "")) return;
    try {
      const updated = await updateGmbReviewText(review.id, text);
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (_) {
      toast.error("No se pudo guardar el texto");
    }
  };

  const handleTranslate = async (review, language) => {
    const text = draftFor(review).trim();
    if (!text) {
      toast.error("No hay texto que traducir");
      return;
    }
    setTranslatingId(review.id);
    try {
      const data = await translateGmbText(text, language);
      const translated = data.translated || "";
      setDrafts((prev) => ({ ...prev, [review.id]: translated }));
      try {
        const updated = await updateGmbReviewText(review.id, translated);
        setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } catch (_) {}
      toast.success(`Traducido a ${language}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo traducir");
    } finally {
      setTranslatingId(null);
    }
  };

  const insertBusinessField = (review, value, label) => {
    const val = (value || "").trim();
    if (!val) {
      toast.error(`Este negocio no tiene ${label.toLowerCase()} guardado`);
      return;
    }
    const current = draftFor(review);
    const sep = !current || current.endsWith(" ") || current.endsWith("\n") ? "" : " ";
    setDrafts((prev) => ({ ...prev, [review.id]: `${current}${sep}${val}` }));
  };

  const openWaModal = (review) => {
    setWaText(buildWhatsAppMessage(review, draftFor(review)));
    setWaPhone(String(review.business_phone || ""));
    setWaModal(review);
  };

  const handleRegenerate = async (review) => {
    setRegeneratingId(review.id);
    try {
      const updated = await regenerateGmbReview(review.id);
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setDrafts((prev) => ({ ...prev, [review.id]: updated.edited_text ?? updated.suggested_text ?? "" }));
      toast.success("Respuesta regenerada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo regenerar");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleApprove = async (review) => {
    const text = draftFor(review).trim();
    if (!text) {
      toast.error("Escribe una respuesta antes de aprobar");
      return;
    }
    setActing(true);
    try {
      const updated = await approveGmbReview(review.id, text);
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Respuesta publicada en Google");
      loadBusinesses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo publicar la respuesta");
      loadReviews();
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (review) => {
    setActing(true);
    try {
      const updated = await rejectGmbReview(review.id);
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Reseña rechazada");
      loadBusinesses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo rechazar");
    } finally {
      setActing(false);
    }
  };

  const startEdit = (review) => {
    setDrafts((prev) => ({ ...prev, [review.id]: review.edited_text ?? review.suggested_text ?? "" }));
    setEditingIds((prev) => new Set(prev).add(review.id));
  };

  const cancelEdit = (review) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(review.id);
      return next;
    });
    setDrafts((prev) => ({ ...prev, [review.id]: review.edited_text ?? review.suggested_text ?? "" }));
  };

  const handleRepublish = async (review) => {
    const text = draftFor(review).trim();
    if (!text) {
      toast.error("La respuesta no puede estar vacía");
      return;
    }
    setActing(true);
    try {
      const updated = await approveGmbReview(review.id, text);
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setEditingIds((prev) => {
        const next = new Set(prev);
        next.delete(review.id);
        return next;
      });
      toast.success("Respuesta actualizada en Google");
      loadBusinesses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar la respuesta");
      loadReviews();
    } finally {
      setActing(false);
      setConfirmRepublish(null);
    }
  };

  const runBatch = async (kind) => {
    const ids = pendingVisible.filter((r) => selectedIds.has(r.id)).map((r) => r.id);
    if (ids.length === 0) return;
    setActing(true);
    const byId = new Map(reviews.map((r) => [r.id, r]));
    const results = await Promise.allSettled(
      ids.map((id) =>
        kind === "approve"
          ? approveGmbReview(id, draftFor(byId.get(id)).trim())
          : rejectGmbReview(id)
      )
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    if (ok) toast.success(`${ok} reseña${ok === 1 ? "" : "s"} ${kind === "approve" ? "publicada(s)" : "rechazada(s)"}`);
    if (fail) toast.error(`${fail} fallaron`);
    setSelectionMode(false);
    setSelectedIds(new Set());
    setActing(false);
    loadReviews();
    loadBusinesses();
  };

  if (authLoading || !user || user.permissions?.gmb === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  const currentBiz = businesses.find((b) => b.business_id === selectedBiz) || null;
  const selectedCount = pendingVisible.filter((r) => selectedIds.has(r.id)).length;
  const dotStyle = getDotPatternStyle(settings);

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="GMB" titleSuffix={currentBiz && !(isMobile && mobileShowList) ? ` / ${currentBiz.name}` : ""} titleAs="h5" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar: negocios/clientes en cajas */}
        <aside className={`${mobileShowList ? "flex" : "hidden md:flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <p className="text-sm font-semibold">Clientes GMB</p>
            <p className="text-xs text-muted-foreground">Las fichas se crean solas al llegar reseñas.</p>
          </div>
          <div className="p-4 flex-1 overflow-auto mobile-nav-scroll">
            {loadingBiz && businesses.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" aria-hidden />
                <span>Cargando…</span>
              </div>
            ) : businesses.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 px-4 text-center text-sm text-muted-foreground">
                <Building2 className="w-8 h-8 text-muted-foreground/50" strokeWidth={1.5} aria-hidden />
                <span>Aún no hay clientes. Aparecerán al recibir la primera reseña.</span>
              </div>
            ) : (
              <ul className="space-y-2" role="list">
                {businesses.map((b) => {
                  const isActive = selectedBiz === b.business_id;
                  return (
                    <li key={b.business_id}>
                      <Card className={`transition-colors shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}>
                        <CardContent className="p-3 flex items-center gap-3">
                          <EmojiPickerPopover value={b.emoji} onSelect={(emoji) => handleSelectEmoji(b.business_id, emoji)}>
                            <button
                              type="button"
                              className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              title="Cambiar icono"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span className="text-2xl leading-none select-none" aria-hidden style={{ textShadow: "rgba(0,0,0,0.3) 0px 1px 2px" }}>
                                {b.emoji || DEFAULT_EMOJI}
                              </span>
                            </button>
                          </EmojiPickerPopover>
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                            onClick={() => { setSelectedBiz(b.business_id); setMobileShowList(false); }}
                          >
                            <p className="text-sm font-medium flex items-center gap-2">
                              <span className="truncate flex-1 min-w-0">{b.name}</span>
                              {b.pending_count > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">
                                  {b.pending_count}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{b.total_count} reseña{b.total_count === 1 ? "" : "s"}</p>
                          </button>
                        </CardContent>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Panel: listado de reseñas del cliente seleccionado */}
        <div className={`${mobileShowList ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col overflow-hidden`} style={dotStyle}>
          <MobileBackBar onBack={() => setMobileShowList(true)} label="Clientes GMB" />
          {!currentBiz ? (
            <div className="h-full flex items-center justify-center px-6 text-center text-muted-foreground">
              <p>Selecciona un cliente de la izquierda para ver sus reseñas.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto mobile-nav-scroll">
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                {/* Cabecera: tabs (estilo Formularios) + iconos de acción */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
                    <TabsList className="rounded-md border border-input bg-background p-0.5 h-11 sm:h-9 w-full sm:w-auto grid grid-cols-3 sm:flex">
                      <TabsTrigger value="neg_pending" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex items-center gap-2 h-full px-1.5 sm:px-3">
                        <span className="sm:hidden">Negativas</span><span className="hidden sm:inline">Negativas Pend.</span>
                        {counts.neg > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">{counts.neg}</span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="pos_pending" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex items-center gap-2 h-full px-1.5 sm:px-3">
                        <span className="sm:hidden">Positivas</span><span className="hidden sm:inline">Positivas Pend.</span>
                        {counts.pos > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">{counts.pos}</span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="all" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm h-full">Todas</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <div className="flex items-center gap-1 flex-wrap ml-auto sm:ml-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="bg-background border border-input h-11 w-11 sm:h-9 sm:w-9"
                          onClick={toggleExpandAll}
                          disabled={visibleReviews.length === 0}
                          aria-label={allExpanded ? "Contraer todo" : "Expandir todo"}
                        >
                          {allExpanded ? <ChevronsDownUp className="w-4 h-4" strokeWidth={1.5} /> : <ChevronsUpDown className="w-4 h-4" strokeWidth={1.5} />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{allExpanded ? "Contraer todo" : "Expandir todo"}</TooltipContent>
                    </Tooltip>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="secondary" size="icon" className="bg-background border border-input h-11 w-11 sm:h-9 sm:w-9" aria-label="Ordenar" title="Ordenar">
                          <ArrowUpDown className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {SORT_OPTIONS.map((opt) => (
                          <DropdownMenuItem key={opt.value} onClick={() => setSortBy(opt.value)} className="flex items-center justify-between gap-2">
                            {opt.label}
                            {sortBy === opt.value && <Check className="w-4 h-4" strokeWidth={1.5} />}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={selectionMode ? "default" : "secondary"}
                          size="icon"
                          className={`h-11 w-11 sm:h-9 sm:w-9 ${selectionMode ? "" : "bg-background border border-input"}`}
                          onClick={() => { setSelectionMode((v) => !v); setSelectedIds(new Set()); }}
                          disabled={pendingVisible.length === 0}
                          aria-label="Seleccionar varias"
                        >
                          <CheckSquare className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Seleccionar varias</TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {/* Barra de acciones batch */}
                {selectionMode && (
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3 rounded-md border bg-muted/40 px-3 py-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <Checkbox checked={allPendingSelected} onCheckedChange={toggleSelectAll} />
                      {selectedCount > 0 ? `${selectedCount} seleccionada${selectedCount === 1 ? "" : "s"}` : "Seleccionar todas"}
                    </label>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="gap-2" onClick={() => runBatch("approve")} disabled={acting || selectedCount === 0}>
                        {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />}
                        Aprobar ({selectedCount})
                      </Button>
                      <Button size="sm" variant="outline" className="gap-2" onClick={() => runBatch("reject")} disabled={acting || selectedCount === 0}>
                        <X className="w-4 h-4" strokeWidth={1.5} />
                        Rechazar ({selectedCount})
                      </Button>
                    </div>
                  </div>
                )}

                {/* Lista */}
                {loadingReviews ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-muted-foreground">
                    <Loader2 className="w-6 h-6 animate-spin" aria-hidden />
                    <span>Cargando reseñas…</span>
                  </div>
                ) : visibleReviews.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
                    <MessageSquare className="w-8 h-8 text-muted-foreground/50" strokeWidth={1.5} aria-hidden />
                    <span>
                      {tab === "neg_pending" ? "No hay negativas pendientes." : tab === "pos_pending" ? "No hay positivas pendientes." : "Sin reseñas todavía."}
                    </span>
                  </div>
                ) : (
                  <ul className="divide-y rounded-lg border bg-card" role="list">
                    {visibleReviews.map((r) => {
                      const stars = ratingToStars(r.rating);
                      const meta = STATUS_META[r.status] || STATUS_META.pending;
                      const isOpen = expandedIds.has(r.id);
                      const canSelect = selectionMode && r.status === "pending";
                      return (
                        <li key={r.id} className={isOpen ? "bg-muted/30" : ""}>
                          {/* Fila */}
                          <div className={`flex items-center gap-3 px-4 py-3 transition-colors ${isOpen ? "bg-muted" : "hover:bg-muted/40"}`}>
                            {canSelect && (
                              <Checkbox
                                checked={selectedIds.has(r.id)}
                                onCheckedChange={() => toggleSelect(r.id)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Seleccionar reseña"
                              />
                            )}
                            <button
                              type="button"
                              className="flex items-center gap-4 flex-1 min-w-0 text-left outline-none"
                              onClick={() => toggleExpand(r)}
                              aria-expanded={isOpen}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{r.reviewer_name || "Anónimo"}</p>
                                <p className="text-xs text-muted-foreground">{formatFecha(r.review_date || r.created_at)}</p>
                                <Badge variant={meta.variant} className="sm:hidden mt-1">{meta.label}</Badge>
                              </div>
                              <Stars value={stars} />
                              <Badge variant={meta.variant} className="shrink-0 hidden sm:inline-flex">{meta.label}</Badge>
                              <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} strokeWidth={1.5} aria-hidden />
                            </button>
                          </div>

                          {/* Detalle desplegable */}
                          {isOpen && (
                            <div className="px-4 pb-4 pt-1 space-y-4">
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Reseña del cliente</p>
                                <blockquote className="border-l-2 border-muted-foreground/30 pl-3 text-sm whitespace-pre-wrap">
                                  {r.comment || "Sin comentario"}
                                </blockquote>
                              </div>

                              <div>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                  <p className="text-xs font-medium text-muted-foreground">Respuesta</p>
                                  {(r.status === "pending" || editingIds.has(r.id)) && (
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="outline" size="sm" className="h-9 w-9 sm:h-7 sm:w-7 p-0 text-xs" aria-label="Insertar dato del negocio" title="Insertar dato del negocio">
                                            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-52">
                                          <DropdownMenuItem onClick={() => insertBusinessField(r, r.business_phone, "Teléfono")}>
                                            <span className="flex-1">Teléfono</span>
                                            <span className="text-xs text-muted-foreground truncate max-w-[110px]">{r.business_phone || "—"}</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => insertBusinessField(r, r.email_contacto, "Email")}>
                                            <span className="flex-1">Email</span>
                                            <span className="text-xs text-muted-foreground truncate max-w-[110px]">{r.email_contacto || "—"}</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => insertBusinessField(r, r.business_name, "Nombre")}>
                                            <span className="flex-1">Nombre</span>
                                            <span className="text-xs text-muted-foreground truncate max-w-[110px]">{r.business_name || "—"}</span>
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-9 sm:h-7 gap-1.5 text-xs"
                                        onClick={() => handleRegenerate(r)}
                                        disabled={regeneratingId === r.id}
                                      >
                                        <RefreshCw className={`w-3.5 h-3.5 ${regeneratingId === r.id ? "animate-spin" : ""}`} strokeWidth={1.5} aria-hidden />
                                        Regenerar
                                      </Button>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="outline" size="sm" className="h-9 sm:h-7 gap-1.5 text-xs" disabled={translatingId === r.id}>
                                            {translatingId === r.id ? (
                                              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                                            ) : (
                                              <Languages className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
                                            )}
                                            Traducir
                                            <ChevronDown className="w-3 h-3 opacity-60" aria-hidden />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-44">
                                          {TRANSLATE_LANGUAGES.map((lang) => (
                                            <DropdownMenuItem key={lang} onClick={() => handleTranslate(r, lang)}>
                                              {lang}
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  )}
                                </div>
                                {(r.status === "pending" || editingIds.has(r.id)) ? (
                                  <Textarea
                                    value={draftFor(r)}
                                    onChange={(e) => setDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                                    onBlur={() => handleSaveDraft(r)}
                                    rows={6}
                                    className="resize-y bg-background"
                                    placeholder="Escribe o edita la respuesta…"
                                  />
                                ) : (
                                  <div className="rounded-md border bg-background p-3 text-sm whitespace-pre-wrap">
                                    {r.edited_text || r.suggested_text || "—"}
                                  </div>
                                )}
                              </div>

                              {r.status === "error" && r.error_message && (
                                <p className="text-sm text-destructive">⚠️ {r.error_message}</p>
                              )}

                              {r.status === "pending" ? (
                                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                                  <Button onClick={() => handleApprove(r)} disabled={acting} className="gap-2 col-span-2 h-11 sm:h-9">
                                    {acting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Check className="w-4 h-4" strokeWidth={1.5} aria-hidden />}
                                    Aprobar y publicar
                                  </Button>
                                  <Button onClick={() => handleReject(r)} disabled={acting} variant="outline" className={`gap-2 h-11 sm:h-9 ${isNegativeReview(r) && r.business_phone ? "" : "col-span-2"}`}>
                                    <X className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                                    Rechazar
                                  </Button>
                                  {isNegativeReview(r) && r.business_phone && (
                                    <button
                                      type="button"
                                      onClick={() => openWaModal(r)}
                                      className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white text-sm font-medium px-3 h-11 sm:h-9 transition-colors"
                                    >
                                      <MessageCircle className="w-4 h-4" strokeWidth={2} aria-hidden />
                                      WhatsApp
                                    </button>
                                  )}
                                </div>
                              ) : editingIds.has(r.id) ? (
                                <div className="flex items-center gap-2">
                                  <Button onClick={() => setConfirmRepublish(r)} disabled={acting} className="gap-2">
                                    {acting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <Check className="w-4 h-4" strokeWidth={1.5} aria-hidden />}
                                    Actualizar en Google
                                  </Button>
                                  <Button onClick={() => cancelEdit(r)} disabled={acting} variant="outline" className="gap-2">
                                    <X className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                                    Cancelar
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-2">
                                  {r.reviewed_by ? (
                                    <p className="text-xs text-muted-foreground">Gestionada por {r.reviewed_by}</p>
                                  ) : <span />}
                                  <div className="flex items-center gap-2">
                                    {isNegativeReview(r) && r.business_phone && (
                                      <button
                                        type="button"
                                        onClick={() => openWaModal(r)}
                                        className="inline-flex items-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1ebe5d] text-white text-xs font-medium px-2.5 h-8 transition-colors"
                                      >
                                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                                        WhatsApp
                                      </button>
                                    )}
                                    {(r.status === "published" || r.status === "error") && (
                                      <Button onClick={() => startEdit(r)} variant="outline" size="sm" className="gap-2">
                                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
                                        Editar respuesta
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmRepublish} onOpenChange={(open) => { if (!open) setConfirmRepublish(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Actualizar la respuesta en Google?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a <strong>sobrescribir</strong> la respuesta ya publicada de esta reseña
              {confirmRepublish?.reviewer_name ? ` (${confirmRepublish.reviewer_name})` : ""}. El cambio se aplica de inmediato en la ficha.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={acting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmRepublish && handleRepublish(confirmRepublish)} disabled={acting}>
              {acting ? "Actualizando…" : "Sí, actualizar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!waModal} onOpenChange={(open) => { if (!open) setWaModal(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Avisar al dueño por WhatsApp</DialogTitle>
            <DialogDescription>
              Revisa y edita el mensaje. Al enviar se abrirá WhatsApp con el chat de
              {waModal?.business_name ? ` ${waModal.business_name}` : " el dueño"} y este texto ya escrito.
            </DialogDescription>
          </DialogHeader>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Enviar al número</p>
            <Input
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
              placeholder="34657905577"
              inputMode="tel"
            />
          </div>
          <Textarea
            value={waText}
            onChange={(e) => setWaText(e.target.value)}
            rows={12}
            className="resize-y"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaModal(null)}>Cancelar</Button>
            <Button
              className="bg-[#25D366] hover:bg-[#1ebe5d] text-white gap-2"
              disabled={!String(waPhone).replace(/[^\d]/g, "")}
              onClick={() => {
                window.open(waUrl(waPhone, waText), "_blank", "noopener,noreferrer");
                setWaModal(null);
              }}
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} aria-hidden />
              Enviar por WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
